import { useEffect, useRef, useState } from 'react';
import {
  ChatPanel,
  MessageBubble,
  Composer,
  IconButton,
  Icon,
  icons,
  Button,
  Callout,
  StatusTag,
  theme,
  type Message,
} from '@agentisai/design-system';
import {
  MAX_ATTACHMENT_BYTES,
  type Attachment,
  type TurnEvent,
  type WorkstreamMode,
} from '@agentisai/shared-types';

/**
 * The platform agents a user may invite into a workstream conversation
 * (mirrors `apps/workstreamagent/api/agents/platform/*` minus `discovery`,
 * which is the pre-creation interview agent only — see `create-chat.ts`).
 * Hand-maintained until the registry is exposed to the BFF (follow-on).
 */
export const INVITABLE_AGENTS: readonly { name: string; label: string }[] = [
  { name: 'architect', label: 'Architect' },
  { name: 'delivery', label: 'Delivery' },
  { name: 'engineer', label: 'Engineer' },
  { name: 'governance', label: 'Governance' },
  { name: 'qa', label: 'QA' },
];

export interface WorkspaceConversationProps {
  title: string;
  /**
   * Passed through to `ChatPanel`. Default true (full-pane conversation);
   * false when rendered as the canonical 40% sibling column beside the
   * `WorkstreamArtifacts` panel (Stage 3c — the DS prototype shell layout).
   */
  fluid?: boolean;
  messages: Message[];
  value?: string;
  onChange?: (value: string) => void;
  onSend?: (value: string) => void;
  sendDisabled?: boolean;
  maxLength?: number;
  pending?: boolean;
  sendError?: string | null;
  /** Currently selected invite-agent hint, or `null` for router-auto. */
  invitedAgent: string | null;
  onInviteAgent: (agentName: string | null) => void;
  /**
   * Librarian routing mode, as last reported by the server. `discovery`
   * locks the conversation to the discovery interview — the invite row is
   * replaced by the exit affordance until the user ends the interview.
   */
  mode?: WorkstreamMode;
  /**
   * Agents that produced the latest reply, in execution order (server-reported
   * `data.agents`). Staged turns read as a pipeline (e.g. Engineer → QA).
   */
  activeAgents?: readonly string[];
  /** Ends the discovery interview (sends the `exitDiscovery` action). */
  onExitDiscovery?: () => void;
  /**
   * Live progress checkpoints for the in-flight turn (ADR-0042) — the latest
   * renders as a status line while `pending`; the full trail sits behind a
   * disclosure. Cleared by the parent when the turn resolves.
   */
  turnEvents?: readonly TurnEvent[];
  /**
   * Consensus-tracker conflict detail for the latest turn, or `null` when the
   * agents agreed. Rendered as a warning banner (Librarian flow step 8b —
   * "surface conflict to user").
   */
  conflict?: string | null;
  /** PDF staged for the next turn (parent-owned, like the composer draft). */
  attachment: Attachment | null;
  /** Stage (file chosen) or clear (`null`) the next turn's PDF. */
  onAttachmentChange: (attachment: Attachment | null) => void;
}

/**
 * WorkspaceConversation — the live conversation for an already-created
 * workstream (post-creation). Composes the same DS primitives as
 * `CreateWorkstreamPanel` (ChatPanel + MessageBubble + Composer), rendered
 * `fluid` inline in the WorkspaceShell main pane rather than in a Modal.
 * State is fully owned by the parent (HomePage) — this component renders only.
 */
export function WorkspaceConversation({
  title,
  fluid = true,
  messages,
  value,
  onChange,
  onSend,
  sendDisabled,
  maxLength,
  pending = false,
  sendError,
  invitedAgent,
  onInviteAgent,
  mode = 'normal',
  activeAgents = [],
  turnEvents = [],
  onExitDiscovery,
  conflict,
  attachment,
  onAttachmentChange,
}: WorkspaceConversationProps) {
  // Auto-scroll to the latest turn — same recipe as CreateWorkstreamPanel
  // (imperative DOM sync, the sanctioned effect+ref use, frontend-architecture R24/R25).
  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let el = bottomRef.current?.parentElement ?? null;
    while (el && el.scrollHeight <= el.clientHeight) el = el.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pending]);

  // Cursor-style mode indicator: which routing mode the Librarian is in, and
  // which agents produced the latest reply (in execution order — a staged
  // build→review turn reads Engineer → QA).
  const agentLabel = (name: string) =>
    INVITABLE_AGENTS.find((a) => a.name === name)?.label ??
    name.charAt(0).toUpperCase() + name.slice(1);
  const statusRow = (
    // role=group (not status): a live region here would announce every turn,
    // and the conflict banner below is the page's one role=status element.
    <div
      role="group"
      aria-label="Conversation status"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.space['1_5'],
        padding: `${theme.space[2]} ${theme.space[4]} 0`,
      }}
    >
      {mode === 'discovery' ? (
        <StatusTag tone="info">Discovery mode</StatusTag>
      ) : invitedAgent ? (
        <StatusTag tone="info">Invited: {agentLabel(invitedAgent)}</StatusTag>
      ) : (
        <StatusTag tone="neutral">Auto routing</StatusTag>
      )}
      {activeAgents.length > 0 && (
        <StatusTag tone="success">Last reply: {activeAgents.map(agentLabel).join(' → ')}</StatusTag>
      )}
    </div>
  );

  // PDF attach — native file input behind the existing paperclip (the ref is
  // the sanctioned uncontrolled-file-input use, frontend-architecture R25).
  // The parent owns the staged attachment; only the pick error is local (R1.0).
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);

  const onFileChosen = (file: File | undefined) => {
    setAttachError(null);
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setAttachError('Only PDF files can be attached.');
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setAttachError(
        `PDF is too large — the limit is ${(MAX_ATTACHMENT_BYTES / (1024 * 1024)).toFixed(1)} MB.`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      // data URL → bare base64 (the wire format AttachmentSchema validates).
      // readAsDataURL always yields a string; the guard narrows the union.
      const result = typeof reader.result === 'string' ? reader.result : '';
      const data = result.split(',')[1] ?? '';
      onAttachmentChange({ name: file.name, mediaType: 'application/pdf', data });
    };
    reader.onerror = () => setAttachError('Could not read that file. Please try again.');
    reader.readAsDataURL(file);
  };

  const composerLeadingActions = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e) => {
          onFileChosen(e.target.files?.[0]);
          // Reset so re-picking the same file fires onChange again.
          e.target.value = '';
        }}
      />
      <IconButton
        aria-label="Attach a PDF"
        size="sm"
        variant="ghost"
        icon={<Icon as={icons.Paperclip} size={14} />}
        onClick={() => fileInputRef.current?.click()}
      />
      <IconButton
        aria-label="Quick actions"
        size="sm"
        variant="ghost"
        icon={<Icon as={icons.Zap} size={14} />}
      />
    </>
  );

  return (
    <ChatPanel
      fluid={fluid}
      caption={
        mode === 'discovery'
          ? 'Discovery interview in progress — the discovery agent leads until you exit'
          : 'Ask a question, or invite a specialist agent below'
      }
      title={title}
      awaitingReply={pending}
      messages={
        <>
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
          <div ref={bottomRef} aria-hidden="true" />
        </>
      }
      composer={
        <>
          {statusRow}
          {pending && turnEvents.length > 0 && (
            // Live turn narration (ADR-0042): the latest checkpoint under the
            // typing indicator, the full trail behind a native disclosure.
            // polite live region — checkpoint granularity (a handful per
            // turn), not a token stream, so announcements stay reasonable.
            <div style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}>
              <details>
                <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
                  <span role="status" aria-live="polite">
                    <StatusTag tone="info">{turnEvents[turnEvents.length - 1]!.label}</StatusTag>
                  </span>
                </summary>
                <ol
                  aria-label="Turn activity"
                  style={{
                    margin: `${theme.space['1_5']} 0 0`,
                    paddingLeft: theme.space[5],
                    fontSize: theme.typography.fontSize.caption,
                    color: theme.color.neutral.muted,
                  }}
                >
                  {turnEvents.map((e, i) => (
                    <li key={`${e.at}-${i}`}>{e.label}</li>
                  ))}
                </ol>
              </details>
            </div>
          )}
          {mode === 'discovery' ? (
            // Discovery locks routing server-side — inviting is inert, so the
            // row is replaced by the one meaningful action: exit the interview.
            // If this chrome grows beyond a single button (banner, distinct
            // composer state), promote it to a DS ChatPanel variant + story (R0).
            <div
              style={{
                display: 'flex',
                gap: theme.space['1_5'],
                padding: `${theme.space[2]} ${theme.space[4]} 0`,
              }}
            >
              <Button size="sm" variant="secondary" disabled={pending} onClick={onExitDiscovery}>
                Exit Discovery Interview
              </Button>
            </div>
          ) : (
            <div
              role="group"
              aria-label="Invite an agent"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: theme.space['1_5'],
                padding: `${theme.space[2]} ${theme.space[4]} 0`,
              }}
            >
              <Button
                size="sm"
                variant={invitedAgent === null ? 'secondary' : 'ghost'}
                aria-pressed={invitedAgent === null}
                onClick={() => onInviteAgent(null)}
              >
                Auto
              </Button>
              {INVITABLE_AGENTS.map((agent) => (
                <Button
                  key={agent.name}
                  size="sm"
                  variant={invitedAgent === agent.name ? 'secondary' : 'ghost'}
                  aria-pressed={invitedAgent === agent.name}
                  onClick={() => onInviteAgent(agent.name)}
                >
                  {agent.label}
                </Button>
              ))}
            </div>
          )}
          {attachment && (
            <div
              role="group"
              aria-label="Attached file"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.space['1_5'],
                padding: `${theme.space[2]} ${theme.space[4]} 0`,
              }}
            >
              <StatusTag tone="info">
                <Icon as={icons.Paperclip} size={12} /> {attachment.name}
              </StatusTag>
              <Button size="sm" variant="ghost" onClick={() => onAttachmentChange(null)}>
                Remove
              </Button>
            </div>
          )}
          {attachError && (
            <div
              role="alert"
              aria-live="assertive"
              style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
            >
              <Callout tone="danger">{attachError}</Callout>
            </div>
          )}
          {conflict && (
            // Advisory, not an error: polite live region so SRs announce the
            // disagreement without interrupting (Callout carries no role itself).
            <div
              role="status"
              aria-live="polite"
              style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
            >
              <Callout tone="warn" title="Agents disagreed on this reply">
                {conflict}
              </Callout>
            </div>
          )}
          {sendError && (
            <div
              role="alert"
              aria-live="assertive"
              style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
            >
              <Callout tone="danger">{sendError}</Callout>
            </div>
          )}
          <Composer
            value={value}
            onChange={onChange}
            onSend={onSend}
            sendDisabled={sendDisabled}
            placeholder={
              mode === 'discovery'
                ? 'Reply to the discovery agent…'
                : invitedAgent
                  ? `Ask ${INVITABLE_AGENTS.find((a) => a.name === invitedAgent)?.label}…`
                  : 'Ask a question…'
            }
            leadingActions={composerLeadingActions}
          />
          {maxLength != null && (value?.length ?? 0) > maxLength && (
            <div
              role="alert"
              aria-live="assertive"
              style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
            >
              <Callout tone="danger">
                Message is too long — {(value?.length ?? 0).toLocaleString()} of{' '}
                {maxLength.toLocaleString()} characters. Shorten it to send.
              </Callout>
            </div>
          )}
        </>
      }
    />
  );
}
WorkspaceConversation.displayName = 'WorkspaceConversation';
